/**
 * Wire types for quality scores (`/runs/{runId}/scores/*`, `/scores/trends`) —
 * mirrored from `apps/api/src/app/schemas/quality_scores.py` (camelCase on the wire).
 *
 * Scores are 0–100. A score is `null` where there is nothing to average (a phase
 * that has not run, a dimension a field has no rules in) and never 0 — 0 is the
 * score of a MISSING field. Deltas are POST minus PRE, `null` unless both exist.
 * Everything is one platform's; enum-like values are read leniently as strings.
 */

/** The three business views of the same rules. GMC: all three · Meta: AEO, GEO · Amazon: SEO, GEO. */
export const SCORE_LENSES = ['SEO', 'AEO', 'GEO'] as const;
export type ScoreLens = (typeof SCORE_LENSES)[number];

export const SCORE_VERDICTS = ['PASS', 'REVIEW', 'FAIL'] as const;
export type ScoreVerdict = (typeof SCORE_VERDICTS)[number];

export const SCORE_DIMENSIONS = [
  'COMPLETENESS',
  'CORRECTNESS',
  'COMPLIANCE',
  'SECURITY',
  'READABILITY',
  'RELEVANCE',
] as const;

/**
 * Feed attributes the scorer reads from the channel's own feed row, beside the content
 * fields a run writes (`CONTENT_FIELDS`). Mirrors the service's `ScoredAttribute`. They are
 * scored on every run and hold the feed value in both phases: enrichment writes none yet.
 */
export const SCORED_ATTRIBUTES = [
  'STRUCTURED_TITLE',
  'STRUCTURED_DESCRIPTION',
  'SHORT_TITLE',
  'PRODUCT_HIGHLIGHT',
  'PRODUCT_DETAIL',
  'PRODUCT_TYPE',
  'BRAND',
  'GTIN',
  'MPN',
  'IDENTIFIER_EXISTS',
  'ITEM_GROUP_ID',
  'COLOR',
  'MATERIAL',
  'PATTERN',
  'SIZE',
  'SIZE_TYPE',
  'SIZE_SYSTEM',
  'GENDER',
  'AGE_GROUP',
  'PRODUCT_LENGTH',
  'PRODUCT_WIDTH',
  'PRODUCT_HEIGHT',
  'PRODUCT_WEIGHT',
  'SHIPPING_LENGTH',
  'SHIPPING_WIDTH',
  'SHIPPING_HEIGHT',
  'SHIPPING_WEIGHT',
  'IS_BUNDLE',
  'MULTIPACK',
  'CONDITION',
  'AVAILABILITY',
  'IMAGE_LINK',
  'ADDITIONAL_IMAGE_LINK',
  'GOOGLE_PRODUCT_CATEGORY',
  'ADDITIONAL_FEATURES',
] as const;
export type ScoredAttribute = (typeof SCORED_ATTRIBUTES)[number];

/** Product list ordering: biggest improvement, worst after, worst before, or by id. */
export type ScoreProductOrder = 'delta' | 'post' | 'pre' | 'product';

export interface LensSummary {
  score: number | null;
  passCount: number;
  reviewCount: number;
  failCount: number;
}

export interface ScorePlatform {
  platform: string;
  products: number;
}

/** One phase (PRE_ENRICHMENT / POST_ENRICHMENT) of a run or batch. */
export interface PhaseSummary {
  phase: string;
  products: number;
  overall: number | null;
  passCount: number;
  reviewCount: number;
  failCount: number;
  failedChecks: number;
  criticalFailures: number;
  missingFields: number;
  scoredFields: number;
  /** `histogram[i]` = products whose overall is in [10i, 10i+10); 100 in the last band. */
  histogram: number[];
  lenses: Record<string, LensSummary>;
}

/** How many products went from one verdict before enrichment to one after. */
export interface VerdictMovement {
  preVerdict: string;
  postVerdict: string;
  products: number;
}

export interface ScoreSummary {
  runId: string;
  batchIndex: number | null;
  platform: string | null;
  platforms: ScorePlatform[];
  pre: PhaseSummary | null;
  post: PhaseSummary | null;
  overallDelta: number | null;
  lensDeltas: Record<string, number | null>;
  movement: VerdictMovement[];
}

/** One before/after cell. */
export interface MatrixCell {
  pre: number | null;
  post: number | null;
  delta: number | null;
  preProducts: number;
  postProducts: number;
}

export interface MatrixField {
  field: string;
  score: MatrixCell;
  dimensions: Record<string, MatrixCell>;
  lenses: Record<string, MatrixCell>;
}

export interface ScoreMatrix {
  platform: string | null;
  fields: MatrixField[];
  overall: MatrixCell;
  dimensions: Record<string, MatrixCell>;
  lenses: Record<string, MatrixCell>;
}

/** One rule across the run: fails before/after, fixed (fail→pass) and regressed (pass→fail). */
export interface CheckScore {
  checkId: string;
  field: string;
  dimension: string;
  scorer: string;
  severity: string;
  threshold: string;
  lenses: string[];
  evaluatedPre: number;
  evaluatedPost: number;
  failsPre: number;
  failsPost: number;
  fixed: number;
  regressed: number;
  examplePostFailure: string;
}

export interface VerticalScore {
  vertical: string;
  products: number;
  pre: number | null;
  post: number | null;
  delta: number | null;
  passAfter: number;
  reviewAfter: number;
  failAfter: number;
}

export interface ProductScore {
  productId: string;
  vertical: string;
  batchIndex: number;
  pre: number | null;
  post: number | null;
  delta: number | null;
  preVerdict: string;
  postVerdict: string;
  failedChecksPre: number;
  failedChecksPost: number;
}

/** One rule's result on one field of one product in one phase. */
export interface CheckResult {
  checkId: string;
  dimension: string;
  scorer: string;
  severity: string;
  passed: boolean;
  score: number;
  measured: string;
  threshold: string;
  message: string;
  lenses: string[];
}

export interface FieldScore {
  field: string;
  score: number | null;
  verdict: string;
  measured: string;
  dimensions: Record<string, number>;
  lenses: Record<string, number>;
  checks: CheckResult[];
}

export interface PhaseScore {
  phase: string;
  overall: number | null;
  verdict: string;
  rulesVersion: string;
  scoreVersion: string;
  dimensions: Record<string, number>;
  lenses: Record<string, number>;
  lensVerdicts: Record<string, string>;
  fields: FieldScore[];
}

/**
 * The scored text. `before` null = catalog unreadable, '' = feed has no value; `after` null =
 * nothing generated. A feed attribute (GTIN, COLOR...) carries the feed value on both sides.
 */
export interface FieldContent {
  field: string;
  before: string | null;
  after: string | null;
}

export interface ProductScoreDetail {
  productId: string;
  runId: string;
  vertical: string;
  platform: string;
  batchIndex: number;
  pre: PhaseScore | null;
  post: PhaseScore | null;
  overallDelta: number | null;
  content: FieldContent[];
}

export interface TrendRun {
  runId: string;
  scoredAt: string;
  products: number;
  overall: MatrixCell;
  lenses: Record<string, MatrixCell>;
  failAfter: number;
}

export interface ScoreTrends {
  platform: string;
  vertical: string | null;
  runs: TrendRun[];
}

/** Scope shared by every run route. `platform` is required only when the run scored several. */
export interface ScoreScope {
  platform?: string | undefined;
  batchIndex?: number | undefined;
}

export interface ScoreProductFilters extends ScoreScope {
  vertical?: string | undefined;
  verdict?: ScoreVerdict | undefined;
  /** Keeps products whose id contains this text (case-insensitive). */
  q?: string | undefined;
  /** Keeps products that failed this rule (`checkId`) in `checkPhase`, on `checkField` if given. */
  failingCheck?: string | undefined;
  checkField?: string | undefined;
  checkPhase?: 'pre' | 'post' | undefined;
  order?: ScoreProductOrder | undefined;
  page?: number | undefined;
  size?: number | undefined;
}

export interface ScoreProductPage {
  items: ProductScore[];
  total: number;
}

/** A rule the Products list is narrowed to: the products that fail it in the latest phase. */
export interface RuleFilter {
  checkId: string;
  field: string;
  /** Plain sentence for the chip ("Size is missing"). */
  label: string;
}
